import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { BadgeCheck, X, Check, UserPlus, Sparkles, Mail, Building2 } from "lucide-react";

// AgencyLinkInbox — surfaces (1) athlete-initiated join requests awaiting
// approval, and (2) a one-time "claim these athletes" prompt for athletes who
// listed this agency/club's name on their profile but haven't sent a request.
// Both flows write to org_link_requests + representations behind the scenes.
//
// Rendered inline at the top of the Agent Dashboard. Hides itself entirely
// when there's nothing to act on, so quiet weeks don't show a chrome shell.

export default function AgencyLinkInbox({ onAfterChange }) {
  const [requests, setRequests] = useState([]);
  const [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState({});
  const [dismissedPrompt, setDismissedPrompt] = useState(false);

  const reload = async () => {
    try {
      const [{ data: inbox }, { data: matchData }] = await Promise.all([
        api.get("/agency/link-request-inbox"),
        api.get("/agency/match-suggestions"),
      ]);
      setRequests(inbox.requests || []);
      setMatches(matchData.candidates || []);
    } catch { /* agency-only endpoints; silent for non-agency users */ }
  };

  useEffect(() => { reload(); }, []);

  const decide = async (id, action) => {
    setBusy(b => ({ ...b, [id]: action }));
    try {
      await api.post(`/agency/link-request/${id}/${action}`);
      await reload();
      onAfterChange?.();
    } finally {
      setBusy(b => { const c = { ...b }; delete c[id]; return c; });
    }
  };

  const claim = async (athlete) => {
    const key = `claim-${athlete.athlete_user_id}-${athlete.kind}`;
    setBusy(b => ({ ...b, [key]: true }));
    try {
      await api.post("/agency/match-suggestions/claim", {
        athlete_user_id: athlete.athlete_user_id,
        kind: athlete.kind,
      });
      await reload();
      onAfterChange?.();
    } finally {
      setBusy(b => { const c = { ...b }; delete c[key]; return c; });
    }
  };

  const dismissMatches = async () => {
    setDismissedPrompt(true);
    try { await api.post("/agency/match-suggestions/dismiss"); } catch { /* fire-and-forget */ }
  };

  const hasRequests = requests.length > 0;
  const hasMatches = matches.length > 0 && !dismissedPrompt;
  if (!hasRequests && !hasMatches) return null;

  return (
    <div className="mb-10 space-y-4" data-testid="agency-link-inbox">
      {hasRequests && (
        <div className="surface p-6 border-volt/40" data-testid="agency-link-requests">
          <div className="label-eyebrow text-volt flex items-center gap-2 mb-4">
            <Mail className="w-3.5 h-3.5" /> Athlete link requests · {requests.length} pending
          </div>
          <div className="space-y-3">
            {requests.map(r => (
              <div key={r.id} className="flex items-center gap-4 border border-line p-3"
                   data-testid={`link-req-${r.id}`}>
                <div className="bg-volt/10 p-2 shrink-0"><Building2 className="w-4 h-4 text-volt" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white truncate">
                    {r.athlete_full_name || r.athlete_email}
                    <span className="text-ink-muted text-xs ml-2 uppercase tracking-widest">as {r.org_kind}</span>
                  </div>
                  <div className="text-xs text-ink-muted">
                    {r.athlete_email} · typed “{r.typed_name}”
                  </div>
                </div>
                <button onClick={() => decide(r.id, "approve")}
                  disabled={!!busy[r.id]}
                  data-testid={`link-req-approve-${r.id}`}
                  className="btn-volt inline-flex items-center gap-1.5 text-xs">
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
                <button onClick={() => decide(r.id, "decline")}
                  disabled={!!busy[r.id]}
                  data-testid={`link-req-decline-${r.id}`}
                  className="btn-ghost-line inline-flex items-center gap-1.5 text-xs">
                  <X className="w-3.5 h-3.5" /> Decline
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasMatches && (
        <div className="surface p-6 border-volt/30" data-testid="agency-match-suggestions">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="label-eyebrow text-volt flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> We found athletes who listed your name
              </div>
              <p className="text-xs text-ink-muted mt-1 leading-relaxed max-w-xl">
                These athletes wrote your organisation name into their profile. Claim them to link the relationship — they&apos;ll see a ✓ verified badge and can unlink anytime.
              </p>
            </div>
            <button onClick={dismissMatches}
              data-testid="match-suggestions-dismiss"
              className="text-xs text-ink-muted hover:text-white inline-flex items-center gap-1">
              <X className="w-3 h-3" /> Don&apos;t show again
            </button>
          </div>
          <div className="space-y-2">
            {matches.map(m => {
              const key = `claim-${m.athlete_user_id}-${m.kind}`;
              return (
                <div key={key} className="flex items-center gap-4 border border-line p-3"
                     data-testid={`match-${m.athlete_user_id}`}>
                  <div className="bg-volt/10 p-2 shrink-0"><BadgeCheck className="w-4 h-4 text-volt" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate">{m.full_name || "Unnamed athlete"}</div>
                    <div className="text-xs text-ink-muted">
                      {m.sport || "Sport not listed"} · typed your name as <span className="uppercase tracking-widest">{m.kind}</span>
                    </div>
                  </div>
                  <button onClick={() => claim(m)}
                    disabled={!!busy[key]}
                    data-testid={`claim-athlete-${m.athlete_user_id}`}
                    className="btn-volt inline-flex items-center gap-1.5 text-xs">
                    <UserPlus className="w-3.5 h-3.5" /> Claim
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
